import { RSA_OAEP_ALGORITHM, RSA_OAEP_PARAMS } from './constants';
import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  getWebCrypto,
  unwrapPem,
  wrapPem,
} from './encoding';
import type { RsaKeyPair } from './types';

async function exportPublicKeySpkiPem(publicKey: CryptoKey): Promise<string> {
  const cryptoApi = getWebCrypto();
  const spki = await cryptoApi.subtle.exportKey('spki', publicKey);
  return wrapPem('PUBLIC KEY', arrayBufferToBase64(spki));
}

export async function exportPrivateKeyPkcs8Pem(privateKey: CryptoKey): Promise<string> {
  const cryptoApi = getWebCrypto();
  const pkcs8 = await cryptoApi.subtle.exportKey('pkcs8', privateKey);
  return wrapPem('PRIVATE KEY', arrayBufferToBase64(pkcs8));
}

export async function importPublicKeyFromPem(publicKeyPem: string): Promise<CryptoKey> {
  const cryptoApi = getWebCrypto();
  const der = base64ToArrayBuffer(unwrapPem(publicKeyPem));

  return cryptoApi.subtle.importKey('spki', der, RSA_OAEP_PARAMS, true, ['encrypt', 'wrapKey']);
}

export async function importPrivateKeyFromPem(privateKeyPem: string): Promise<CryptoKey> {
  const cryptoApi = getWebCrypto();
  const der = base64ToArrayBuffer(unwrapPem(privateKeyPem));

  return cryptoApi.subtle.importKey('pkcs8', der, RSA_OAEP_PARAMS, true, ['decrypt', 'unwrapKey']);
}

export async function importPrivateKey(privateKey: CryptoKey | string): Promise<CryptoKey> {
  return typeof privateKey === 'string' ? importPrivateKeyFromPem(privateKey) : privateKey;
}

async function generateRsaKeyMaterial(): Promise<CryptoKeyPair> {
  const cryptoApi = getWebCrypto();

  return cryptoApi.subtle.generateKey(RSA_OAEP_ALGORITHM, true, [
    'encrypt',
    'decrypt',
    'wrapKey',
    'unwrapKey',
  ]);
}

export async function generateRsaKeyPair(): Promise<RsaKeyPair> {
  const keyPair = await generateRsaKeyMaterial();
  const [publicKeyPem, privateKeyPem] = await Promise.all([
    exportPublicKeySpkiPem(keyPair.publicKey),
    exportPrivateKeyPkcs8Pem(keyPair.privateKey),
  ]);

  return {
    publicKey: keyPair.publicKey,
    privateKey: keyPair.privateKey,
    publicKeyPem,
    privateKeyPem,
  };
}

export async function generateEphemeralKeyPair(): Promise<RsaKeyPair> {
  return generateRsaKeyPair();
}

export async function rsaOaepEncrypt(
  data: BufferSource,
  publicKey: CryptoKey | string,
): Promise<ArrayBuffer> {
  const cryptoApi = getWebCrypto();
  const key = typeof publicKey === 'string' ? await importPublicKeyFromPem(publicKey) : publicKey;

  return cryptoApi.subtle.encrypt(RSA_OAEP_PARAMS, key, data);
}

export async function rsaOaepDecrypt(
  ciphertext: ArrayBuffer,
  privateKey: CryptoKey | string,
): Promise<ArrayBuffer> {
  const cryptoApi = getWebCrypto();
  const key = await importPrivateKey(privateKey);

  return cryptoApi.subtle.decrypt(RSA_OAEP_PARAMS, key, ciphertext);
}
